import {encodeSecp256k1Pubkey} from "@cosmjs/amino";
import {fromBase64} from "@cosmjs/encoding";
import {Int53, Uint53} from "@cosmjs/math";
import {TendermintClient} from "@cosmjs/tendermint-rpc";
import {assert, assertDefined, isUint8Array} from "@cosmjs/utils";
import {AminoConverters, AminoTypes, makeSignDoc as makeSignDocAmino, StdFee} from "@shareledgerjs/amino";
import {
  EncodeObject,
  encodePubkey,
  GeneratedType,
  isOfflineDirectSigner,
  makeAuthInfoBytes,
  makeSignDoc,
  OfflineSigner,
  Registry,
  TxBodyEncodeObject
} from "@shareledgerjs/signing";
import {SignMode} from "@shareledgerjs/types/cosmos/tx/signing/v1beta1/signing";
import {TxRaw} from "@shareledgerjs/types/cosmos/tx/v1beta1/tx";

import {ClientOptions} from "./baseclient";
import {Client, DeliverTxResponse} from "./client";
import {calculateFee, GasPrice} from "./fee";
import {createAuthTypes} from "./modules/auth";
import {createBankActions, createBankAminoConverters, createBankTypes} from "./modules/bank";
import {createDistributionActions, createDistributionAminoConverters, createDistributionTypes} from "./modules/distribution";
import {createGovActions, createGovAminoConverters, createGovTypes} from "./modules/gov";
import {createSlashingActions, createSlashingAminoConverters, createSlashingTypes} from "./modules/slashing";
import {createStakingActions, createStakingAminoConverters, createStakingTypes} from "./modules/staking";

/**
 * Signing information for a single signer that is not included in the transaction.
 *
 * @see https://github.com/cosmos/cosmos-sdk/blob/v0.42.2/x/auth/signing/sign_mode_handler.go#L23-L37
 */
export interface SignerData {
  readonly accountNumber: number;
  readonly sequence: number;
  readonly chainId: string;
}

export interface SigningOptions extends ClientOptions {
  readonly registry?: Registry;
  readonly aminoTypes?: AminoTypes;
  readonly broadcastTimeoutMs?: number;
  readonly broadcastPollIntervalMs?: number;
  readonly gasPrice?: GasPrice;
}

/** */

export const defaultRegistryTypes: ReadonlyArray<[string, GeneratedType]> = [
  createAuthTypes,
  createBankTypes,
  createDistributionTypes,
  createGovTypes,
  createSlashingTypes,
  createStakingTypes
].reduce<ReadonlyArray<[string, GeneratedType]>>((prev, curr) => [...prev, ...curr()], []);

function createDefaultRegistry(): Registry {
  return new Registry(defaultRegistryTypes);
}

export function createDefaultAminoTypes(): AminoConverters {
  return [
    createBankAminoConverters,
    createDistributionAminoConverters,
    createGovAminoConverters,
    createSlashingAminoConverters,
    createStakingAminoConverters
  ].reduce((prev, curr) => ({...prev, ...curr()}), {});
}

export const defaultActions: Record<string, string> = [
  createBankActions,
  createDistributionActions,
  createGovActions,
  createSlashingActions,
  createStakingActions
].reduce((prev, curr) => ({...prev, ...curr()}), {});

export class SigningClient extends Client {
  public readonly registry: Registry;
  public readonly broadcastTimeoutMs?: number;
  public readonly broadcastPollIntervalMs?: number;

  protected readonly gasPrice?: GasPrice;

  private signer?: OfflineSigner;
  private readonly aminoTypes: AminoTypes;

  // /**
  //  * Creates a client in offline mode.
  //  *
  //  * This should only be used in niche cases where you know exactly what you're doing,
  //  * e.g. when building an offline signing application.
  //  *
  //  * When you try to use online functionality with such a signer, an
  //  * exception will be raised.
  //  */
  // public static async offline(signer: OfflineSigner, options: SigningOptions = {}): Promise<SigningClient> {
  //   return new SigningClient(undefined, signer, options);
  // }

  public constructor(tmClient: TendermintClient | undefined, signer?: OfflineSigner, options: SigningOptions = {}) {
    super(tmClient, options);
    const {registry = createDefaultRegistry(), aminoTypes = new AminoTypes(createDefaultAminoTypes())} = options;
    this.registry = registry;
    this.aminoTypes = aminoTypes;
    this.signer = signer;
    this.broadcastTimeoutMs = options.broadcastTimeoutMs;
    this.broadcastPollIntervalMs = options.broadcastPollIntervalMs;
    this.gasPrice = options.gasPrice;
  }

  protected forceGetSigner(): OfflineSigner {
    if (!this.signer) {
      throw new Error("Signer not available. You must connect with a signer or add a signer to continue.");
    }
    return this.signer;
  }

  public async withSigner(signer: OfflineSigner): Promise<SigningClient> {
    this.signer = signer;
    return this;
  }

  public withoutSigner(): SigningClient {
    this.signer = undefined;
    return this;
  }

  public getSignerAccounts() {
    return this.forceGetSigner().getAccounts();
  }

  public async getSignerAccount() {
    const [account] = await this.getSignerAccounts();
    return account;
  }

  public async simulate(
    signerAddress: string,
    messages: readonly EncodeObject[],
    memo?: string,
    fee?: StdFee,
    explicitSignerData?: SignerData
  ): Promise<number> {
    const anyMsgs = messages.map((m) => this.registry.encodeAsAny(m));
    const accountFromSigner = (await this.forceGetSigner().getAccounts()).find((account) => account.address === signerAddress);
    if (!accountFromSigner) {
      throw new Error("Failed to retrieve account from signer");
    }
    const pubkey = encodeSecp256k1Pubkey(accountFromSigner.pubkey);
    let sequence = 0;
    if (explicitSignerData) {
      sequence = explicitSignerData.sequence;
    } else {
      sequence = await this.getSequence(signerAddress).then((res) => res.sequence);
    }

    const {gasInfo} = await this.tx.simulate(pubkey, sequence, anyMsgs, memo, fee);
    assertDefined(gasInfo);
    return Uint53.fromString(gasInfo.gasUsed.toString()).toNumber();
  }

  public async signAndBroadcast(
    signerAddress: string,
    messages: readonly EncodeObject[] | Uint8Array,
    fee?: (StdFee & {gasPrice?: GasPrice}) | number,
    memo?: string,
    explicitSignerData?: SignerData
  ): Promise<DeliverTxResponse> {
    const txBytes = await this.sign(signerAddress, messages, fee, memo, explicitSignerData);
    return this.broadcastTx(txBytes, this.broadcastTimeoutMs, this.broadcastPollIntervalMs);
  }

  /**
   * This method is useful if you want to send a transaction in broadcast,
   * without waiting for it to be placed inside a block, because for example
   * I would like to receive the hash to later track the transaction with another tool.
   * @returns Returns the hash of the transaction
   */
  public async signAndBroadcastSync(
    signerAddress: string,
    messages: readonly EncodeObject[] | Uint8Array,
    fee?: (StdFee & {gasPrice?: GasPrice}) | number,
    memo?: string,
    explicitSignerData?: SignerData
  ): Promise<string> {
    const txBytes = await this.sign(signerAddress, messages, fee, memo, explicitSignerData);
    return this.broadcastTxSync(txBytes);
  }

  /**
   * Gets account number and sequence from the API, creates a sign doc,
   * creates a single signature and assembles the signed transaction.
   *
   * The sign mode (SIGN_MODE_DIRECT or SIGN_MODE_LEGACY_AMINO_JSON) is determined by this client's signer.
   *
   * You can pass signer data (account number, sequence and chain ID) explicitly instead of querying them
   * from the chain. This is needed when signing for a multisig account, but it also allows for offline signing
   * (See the SigningStargateClient.offline constructor).
   */
  public async sign(
    signerAddress: string,
    messages: readonly EncodeObject[] | Uint8Array,
    fee?: (StdFee & {gasPrice?: GasPrice}) | number,
    memo?: string,
    explicitSignerData?: SignerData
  ): Promise<Uint8Array> {
    let signerData: SignerData;
    if (explicitSignerData) {
      signerData = explicitSignerData;
    } else {
      const {accountNumber, sequence} = await this.getSequence(signerAddress);
      const chainId = await this.getChainId();
      signerData = {
        accountNumber: accountNumber,
        sequence: sequence,
        chainId: chainId
      };
    }

    if (!fee || typeof fee === "number" || (!fee.amount && !fee.gas)) {
      const gasPrice = typeof fee !== "number" ? fee?.gasPrice || this.gasPrice : this.gasPrice;
      assertDefined(gasPrice, "Gas price must be set in the client options when fee amount and gas is not specified.");
      if (Buffer.isBuffer(messages)) {
        messages = Uint8Array.prototype.slice.call(messages);
      }
      const msgs = isUint8Array(messages) || Buffer.isBuffer(messages) ? this.registry.decodeTxBody(messages).messages : messages;
      const multiplier = typeof fee === "number" ? fee : 1.275;
      fee = typeof fee === "number" ? {} : fee;
      const gasEstimation = await this.simulate(signerAddress, msgs, memo, fee, signerData);
      const buff = Math.round(gasEstimation * multiplier);
      fee = {
        ...fee,
        ...calculateFee(buff, gasPrice)
      };
    }
    assert(fee);
    const signer = this.forceGetSigner();
    return isOfflineDirectSigner(signer)
      ? this.signDirect(signerAddress, messages, fee, memo ?? "", signerData)
      : this.signAmino(signerAddress, messages, fee, memo ?? "", signerData);
  }

  private async signAmino(
    signerAddress: string,
    messages: readonly EncodeObject[] | Uint8Array,
    fee: StdFee,
    memo: string,
    {accountNumber, sequence, chainId}: SignerData
  ): Promise<Uint8Array> {
    assert(fee.amount);
    assert(fee.gas);
    const signer = this.forceGetSigner();
    assert(!isOfflineDirectSigner(signer));
    const accountFromSigner = (await signer.getAccounts()).find((account) => account.address === signerAddress);
    if (!accountFromSigner) {
      throw new Error("Failed to retrieve account from signer");
    }
    const pubkey = encodePubkey(encodeSecp256k1Pubkey(accountFromSigner.pubkey));
    const signMode = SignMode.SIGN_MODE_LEGACY_AMINO_JSON;
    let signedTxBodyBytes: Uint8Array = Uint8Array.from([]);
    if (Buffer.isBuffer(messages)) {
      messages = Uint8Array.prototype.slice.call(messages);
    }
    if (isUint8Array(messages) || Buffer.isBuffer(messages)) {
      signedTxBodyBytes = messages;
      messages = this.registry.decodeTxBody(messages).messages;
    } else {
      signedTxBodyBytes = this.getTxBody(messages, memo);
    }
    const msgs = messages.map((msg) => this.aminoTypes.toAmino(msg));
    const signDoc = makeSignDocAmino(msgs, fee, chainId, memo, accountNumber, sequence);
    const {signature, signed} = await signer.signAmino(signerAddress, signDoc);
    assert(signed.fee.amount);
    assert(signed.fee.gas);
    const signedGasLimit = Int53.fromString(signed.fee.gas).toNumber();
    const signedSequence = Int53.fromString(signed.sequence).toNumber();
    const signedAuthInfoBytes = makeAuthInfoBytes(
      [{pubkey, sequence: signedSequence}],
      signed.fee.amount,
      signedGasLimit,
      signed.fee.granter,
      signed.fee.payer,
      signMode
    );
    return TxRaw.encode(
      TxRaw.fromPartial({
        bodyBytes: signedTxBodyBytes,
        authInfoBytes: signedAuthInfoBytes,
        signatures: [fromBase64(signature.signature)]
      })
    ).finish();
  }

  private async signDirect(
    signerAddress: string,
    messages: readonly EncodeObject[] | Uint8Array,
    fee: StdFee,
    memo: string,
    {accountNumber, sequence, chainId}: SignerData
  ): Promise<Uint8Array> {
    assert(fee.amount);
    assert(fee.gas);
    const signer = this.forceGetSigner();
    assert(isOfflineDirectSigner(signer));
    const accountFromSigner = (await signer.getAccounts()).find((account) => account.address === signerAddress);
    if (!accountFromSigner) {
      throw new Error("Failed to retrieve account from signer");
    }
    const pubkey = encodePubkey(encodeSecp256k1Pubkey(accountFromSigner.pubkey));
    if (Buffer.isBuffer(messages)) {
      messages = Uint8Array.prototype.slice.call(messages);
    }
    const txBodyBytes = isUint8Array(messages) || Buffer.isBuffer(messages) ? messages : this.getTxBody(messages, memo);
    const gasLimit = Int53.fromString(fee.gas).toNumber();
    const authInfoBytes = makeAuthInfoBytes([{pubkey, sequence}], fee.amount, gasLimit, fee.granter, fee.payer);
    const signDoc = makeSignDoc(txBodyBytes, authInfoBytes, chainId, accountNumber);
    const {signature, signed} = await signer.signDirect(signerAddress, signDoc);
    return TxRaw.encode(
      TxRaw.fromPartial({
        bodyBytes: signed.bodyBytes,
        authInfoBytes: signed.authInfoBytes,
        signatures: [fromBase64(signature.signature)]
      })
    ).finish();
  }

  public getTxBody(messages: readonly EncodeObject[], memo?: string) {
    const txBodyEncodeObject: TxBodyEncodeObject = {
      typeUrl: "/cosmos.tx.v1beta1.TxBody",
      value: {
        messages: messages,
        memo: memo
      }
    };
    return this.registry.encode(txBodyEncodeObject);
  }
}
